import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Stack,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  CheckCircle as SuccessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { api } from '../config/api';
import ContainerNav from './ContainerNav';

interface LookupStats {
  total: number;
  found: number;
  distinctPos: number;
}

const BatchTraceability: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<LookupStats | null>(null);

  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setError(null);
    setStats(null);
  };

  const handleReset = () => {
    setFile(null);
    setStats(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!file) return;
    setSubmitting(true);
    setError(null);
    setStats(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/traceability/batch-lookup', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
        timeout: 120000,
      });

      // Trigger download. Prefer the filename the server proposes via
      // Content-Disposition; fall back to "<original>_traced.xlsx".
      const cd: string = response.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const fallback = file.name.replace(/\.xlsx$/i, '_traced.xlsx');
      const downloadName = match ? match[1] : fallback;

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const total = parseInt(response.headers['x-traceability-total'] || '0', 10);
      const found = parseInt(response.headers['x-traceability-found'] || '0', 10);
      const distinctPos = parseInt(response.headers['x-traceability-distinct-pos'] || '0', 10);
      setStats({ total, found, distinctPos });
    } catch (err: any) {
      // Error response body is a Blob — try to read it as JSON for a useful message.
      let message = 'Failed to process batch traceability lookup';
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) message = parsed.error;
        } catch {
          /* leave default */
        }
      } else if (err?.message) {
        message = err.message;
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3}>
      <ContainerNav title="Batch Traceability" />

      <Paper sx={{ p: 3, mb: 3, maxWidth: 720 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <SearchIcon color="primary" />
          <Typography variant="h6">Trace raw-material batches back to PO &amp; container</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Upload a standardised <strong>Raw Material Batch Codes</strong> Excel file (must contain
          a <code>Batch Code</code> column). The system looks up each batch in the packing-list
          history and returns the same file enriched with PO number, stock code, container number,
          delivery day, plus per-batch <strong>Sheets</strong> and <strong>Net Weight (t)</strong>
          columns with totals at the bottom. Cells in the <strong>PO Number</strong> column are
          colour-coded so you can see at a glance how many POs are represented.
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          style={{ display: 'none' }}
          onChange={handleFilePicked}
        />

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting}
          >
            Choose file
          </Button>

          {file && (
            <Chip
              icon={<FileIcon />}
              label={file.name}
              onDelete={submitting ? undefined : handleReset}
              variant="outlined"
            />
          )}

          <Button
            variant="contained"
            color="primary"
            disabled={!file || submitting}
            onClick={handleSubmit}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          >
            {submitting ? 'Looking up…' : 'Run lookup'}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 720 }}>
          {error}
        </Alert>
      )}

      {stats && (
        <Paper sx={{ p: 3, maxWidth: 720 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <SuccessIcon color="success" />
            <Typography variant="h6">Lookup complete — file downloaded</Typography>
          </Stack>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <Chip label={stats.total} color="default" />
              </ListItemIcon>
              <ListItemText primary="Batch codes scanned" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip
                  label={stats.found}
                  color={stats.found > 0 ? 'success' : 'default'}
                />
              </ListItemIcon>
              <ListItemText
                primary="Resolved to a PO"
                secondary={
                  stats.found < stats.total
                    ? `${stats.total - stats.found} not found (may pre-date packing-list history)`
                    : 'All batches matched'
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip label={stats.distinctPos} color="primary" />
              </ListItemIcon>
              <ListItemText primary="Distinct POs involved" />
            </ListItem>
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default BatchTraceability;
